import { useState, useMemo, useRef } from 'react';

/**
 * Shared roster editor: search rankings to add players, import CSV, remove rows.
 * @param {Array} roster - Current roster (LeagueRosterDTO objects)
 * @param {Function} onChange - Called with updated roster array
 * @param {Array} [rankings] - Aggregated rankings for player search (optional)
 */
function RosterBuilder({ roster, onChange, rankings = [] }) {
  const [search, setSearch] = useState('');
  const [csvError, setCsvError] = useState(null);
  const fileInputRef = useRef(null);

  const existingNames = useMemo(() => new Set(roster.map(p => p.playerName)), [roster]);

  const searchResults = useMemo(() => {
    if (!search.trim()) return [];
    const q = search.toLowerCase();
    return rankings
      .filter(r =>
        !existingNames.has(r.playerName) &&
        (r.playerName.toLowerCase().includes(q) || (r.position || '').toLowerCase() === q)
      )
      .slice(0, 10);
  }, [search, rankings, existingNames]);

  function addPlayer(r) {
    onChange([...roster, {
      playerName: r.playerName,
      position: r.position || '',
      nflTeam: r.team || '',
      acquisitionType: 'manual',
    }]);
    setSearch('');
  }

  function removePlayer(name) {
    onChange(roster.filter(p => p.playerName !== name));
  }

  function handleCsvImport(e) {
    const file = e.target.files[0];
    if (!file) return;
    setCsvError(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split(/\r?\n/);
      const added = [];
      let skipped = 0;
      lines.forEach((line, i) => {
        const cols = line.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
        const name = cols[0];
        if (!name || name.toLowerCase() === 'player name' || name.toLowerCase() === 'player') return;
        if (!name) { skipped++; return; }
        added.push({
          playerName: name,
          position: cols[1] || '',
          nflTeam: cols[2] || '',
          acquisitionType: 'manual',
        });
      });
      if (skipped > 0) setCsvError(`${skipped} row${skipped > 1 ? 's' : ''} skipped (missing player name)`);
      onChange([...roster, ...added]);
    };
    reader.readAsText(file);
    // Reset so same file can be re-imported
    e.target.value = '';
  }

  return (
    <div>
      {/* Search + CSV import */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input
          className='search-input'
          style={{ flex: 1 }}
          placeholder='Search players to add...'
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button
          className='btn btn-secondary'
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => fileInputRef.current?.click()}
          title='Import from CSV (Player Name, Position, NFL Team)'
        >
          Import CSV
        </button>
        <input
          ref={fileInputRef}
          type='file'
          accept='.csv,text/csv'
          style={{ display: 'none' }}
          onChange={handleCsvImport}
        />
      </div>

      {csvError && (
        <p style={{ color: 'var(--warning, orange)', fontSize: '0.85rem', margin: '4px 0 8px' }}>
          {csvError}
        </p>
      )}

      {/* Search results dropdown */}
      {searchResults.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
          {searchResults.map((r, i) => (
            <div
              key={r.playerName}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 12px',
                borderBottom: i < searchResults.length - 1 ? '1px solid var(--border)' : undefined,
                background: 'var(--surface, #fff)',
              }}
            >
              <span>
                {r.playerName}
                <span style={{ color: 'var(--text-muted)', marginLeft: 8, fontSize: '0.85rem' }}>
                  {r.position}{r.team ? ` · ${r.team}` : ''}
                </span>
              </span>
              <button className='filter-pill' onClick={() => addPlayer(r)}>Add</button>
            </div>
          ))}
        </div>
      )}

      {/* Current roster */}
      {roster.length > 0 ? (
        <table style={{ width: '100%', fontSize: '0.9rem', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Player', 'Pos', 'Team', ''].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--text-muted)', borderBottom: '1px solid var(--border)' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {roster.map((p, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '6px 8px' }}>{p.playerName}</td>
                <td style={{ padding: '6px 8px' }}>{p.position}</td>
                <td style={{ padding: '6px 8px' }}>{p.nflTeam}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                  <button
                    className='filter-pill'
                    style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                    onClick={() => removePlayer(p.playerName)}
                    title='Remove player'
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '24px 0' }}>
          No players yet. Search above or import a CSV.
        </p>
      )}
    </div>
  );
}

export default RosterBuilder;
